<template>
  <!-- 一组兄弟 = 一个 draggable；group 名按父 id 区分且禁止 pull/put，跨父只能走「移动到…」（spec §6.3、派生点 c）。
       顶级那组就是 .act-list（父组件的滚动容器，样式在 AssetCategoryTree 里），子级组用 .act-children -->
  <draggable
    v-model="siblings"
    :class="depth === 1 ? 'act-list' : 'act-children'"
    handle=".act-drag-handle"
    animation="200"
    :group="dragGroup"
    :disabled="!editable || reordering"
    @end="onDragEnd"
  >
    <div v-for="c in siblings" :key="c.id" class="act-node">
      <div
        class="act-item"
        :class="{ 'is-active': value === c.id }"
        :style="{ paddingLeft: rowPaddingLeft }"
        :data-depth="depth"
        :data-id="c.id"
        @click="$emit('select', c)"
      >
        <!-- 展开箭头绝对定位在行左侧的留白里，不占 flex 空间：顶级行的名字 / 数量列才能与「未分类」对齐
             （asset-category-tree-align.cy.ts 守着这条） -->
        <button
          v-if="hasChildren(c)"
          type="button"
          class="act-expand"
          :style="{ left: expandLeft }"
          :aria-expanded="!collapsed[c.id]"
          :aria-label="collapsed[c.id] ? '展开分组' : '收起分组'"
          @click.stop="$emit('toggle', c)"
        >
          <i :class="collapsed[c.id] ? 'el-icon-caret-right' : 'el-icon-caret-bottom'" />
        </button>
        <i v-if="editable" class="act-drag-handle el-icon-rank" @click.stop />
        <!-- 名字放不下就折行、不截断（2026-10-16），全名始终可见，不再需要 title 悬停提示 -->
        <span class="act-name">{{ c.name }}</span>
        <span class="act-count">{{ c.count }}</span>
        <el-dropdown v-if="editable" trigger="click" @command="$emit('command', c, $event)">
          <span class="act-edit-icon" title="操作" @click.stop><i class="el-icon-edit"></i></span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item v-if="canAddChild(categories, c.id)" command="child">新增子分组</el-dropdown-item>
            <el-dropdown-item command="rename">改名</el-dropdown-item>
            <el-dropdown-item v-if="hierarchy" command="move">移动到…</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <span v-if="editable" class="act-edit-icon" title="删除" @click.stop="$emit('command', c, 'delete')">
          <i class="el-icon-delete"></i>
        </span>
      </div>
      <AssetCategoryBranch
        v-if="hasChildren(c) && !collapsed[c.id]"
        :categories="categories"
        :parent-id="c.id"
        :depth="depth + 1"
        :value="value"
        :editable="editable"
        :collapsed="collapsed"
        :reordering="reordering"
        v-on="$listeners"
      />
    </div>
  </draggable>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import draggable from 'vuedraggable'
import type { AssetCategoryView } from '@/api/asset'
import { categoryChildren, canAddChild, supportsCategoryHierarchy } from '@/utils/assetCategoryHierarchy'

/**
 * 分类树的一层：渲染 parentId 下的全部兄弟，每行下面递归渲染它自己的子级。
 * 不持有数据、不发请求：选中 / 展开 / 行内操作 / 排序都 emit 给 AssetCategoryTree，
 * 子级分支用 v-on="$listeners" 把事件原样往上冒。
 */
@Component({ name: 'AssetCategoryBranch', components: { draggable } })
export default class AssetCategoryBranch extends Vue {
  @Prop({ required: true }) readonly categories!: AssetCategoryView[]
  @Prop({ default: null }) readonly parentId!: number | null
  @Prop({ default: 1 }) readonly depth!: number
  @Prop() readonly value?: number
  @Prop({ required: true }) readonly editable!: boolean
  @Prop({ default: () => ({}) }) readonly collapsed!: Record<number, boolean>
  @Prop({ default: false }) readonly reordering!: boolean

  // vuedraggable 必须 v-model 一个可写数组：本地保存兄弟顺序，数据源变化时从 prop 重新过滤，
  // 排序成功后靠父组件 changed -> reload() 换回权威数据再经 watch 同步回来
  siblings: AssetCategoryView[] = []

  canAddChild = canAddChild

  @Watch('categories', { immediate: true })
  onCategoriesChange() {
    this.sync()
  }

  @Watch('parentId')
  onParentChange() {
    this.sync()
  }

  sync() {
    this.siblings = categoryChildren(this.categories || [], this.parentId)
  }

  get hierarchy(): boolean {
    return supportsCategoryHierarchy(this.categories || [])
  }

  get dragGroup(): { name: string; pull: boolean; put: boolean } {
    return { name: `asset-cat-${this.parentId === null ? 'root' : this.parentId}`, pull: false, put: false }
  }

  /** 顶级 12px 与原 .act-item 的左内边距一致，每深一级多缩进 16px */
  get rowPaddingLeft(): string {
    return `${12 + (this.depth - 1) * 16}px`
  }

  get expandLeft(): string {
    return `${(this.depth - 1) * 16 - 2}px`
  }

  hasChildren(c: AssetCategoryView): boolean {
    return categoryChildren(this.categories || [], c.id).length > 0
  }

  // 拖完把本组兄弟的 id 按新顺序交给父组件发 reorder（只改这几行的 sort，spec §5.7）
  onDragEnd() {
    this.$emit('reorder', this.siblings.map(c => c.id))
  }
}
</script>

<style scoped lang="scss">
.act-children {
  // 缩进由每行的 padding-left 承担（行要整宽高亮），这里不再加内边距
  padding-left: 0;
}

.act-item {
  position: relative;
  display: flex;
  // 名字放不下时折行（2026-10-16 用户要求名字展示全，不再截成省略号）。手柄、数量、操作图标
  // 一律顶对齐，配合下面写死的行高落在名字的第一行上，而不是多行名字的垂直中线上
  align-items: flex-start;
  padding: 8px 12px;
  margin-bottom: 2px;
  border-radius: 6px;
  font-size: 13px;
  // 行高写死：原来的 normal 跟着字体走，中文名和纯英文名的行内容实测差 2px，顶对齐后手柄 / 图标
  // 会跟第一行错开。19px 是原来单行时行内容的实测高度，单行的行高（35px）不变
  line-height: 19px;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(99, 102, 241, 0.06);

    .act-drag-handle,
    .act-edit-icon {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &.is-active {
    background: rgba(99, 102, 241, 0.1);
    color: #6366f1;
    font-weight: 600;

    .act-count {
      color: #6366f1;
    }
  }
}

// 原来不写 top，靠 align-items: center 的静态位置居中，名字一折行箭头就落到两行中间；
// 改成 top = 行的上内边距、高度 = 一行，按钮内容自己垂直居中，箭头始终对着第一行
.act-expand {
  position: absolute;
  top: 8px;
  height: 19px;
  width: 16px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #909399;
  cursor: pointer;
  line-height: 1;
}

.act-drag-handle {
  flex-shrink: 0;
  margin-right: 4px;
  // Element 图标自带 line-height: 1（只有 13px 高），顶对齐后会比第一行的字偏上
  line-height: inherit;
  color: #9ca3af;
  cursor: grab;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

// 不截断，放不下就折行。弹窗 body 自带 word-break: break-all（Element .el-dialog__body），
// 有空格的英文名也会被从单词中间劈开；改回 normal 再配 anywhere：有空格就在空格处换行，
// 没空格的长串（文件名式的分组名）才在任意处断开，不会顶出行外
.act-name {
  flex: 1;
  min-width: 0;
  word-break: normal;
  overflow-wrap: anywhere;
}

.act-count {
  flex-shrink: 0;
  margin-left: 8px;
  color: #9ca3af;
  font-size: 12px;
}

.act-edit-icon {
  flex-shrink: 0;
  margin-left: 6px;
  // 只留左右内边距：删除图标的 span 本身是 flex 项，上下各 2px 会把它撑高到 23px——顶对齐后
  // 比第一行低 2px，单行的行高也会跟着变高（铅笔那个在 el-dropdown 里是行内元素，不受影响）
  padding: 0 2px;
  color: #9ca3af;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, color 0.15s ease;

  &:hover {
    color: #6366f1;
  }
}
</style>
